/**
 * <sun-menu-overlay>
 *
 * Full-page menu overlay for Sun Society.
 * SSR-first: Liquid renders all DOM, this component only toggles state.
 *
 * State machine (data-attributes on the custom element root):
 *   - data-open="true|false"                 — overlay visible
 *   - data-active-n2="<normalized-title>"    — active N2 panel (empty = none)
 *   - data-push-active="global|<n2-handle>"  — which pushes are shown
 *
 * Trigger selectors (external):
 *   - [data-sun-menu-toggle]  — open the overlay
 *   - [data-sun-menu-close]   — close the overlay
 *
 * Depends on window.theme.a11y (trapFocus / removeTrapFocus) and
 * theme:scroll:lock / theme:scroll:unlock custom events.
 */
(function () {
  if (customElements.get('sun-menu-overlay')) return;

  const ESC_KEY = 'Escape';

  class SunMenuOverlay extends HTMLElement {
    constructor() {
      super();
      this.onExternalToggleClick = this.onExternalToggleClick.bind(this);
      this.onInnerClick = this.onInnerClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.setAttribute('data-open', 'false');
      this.setAttribute('data-active-n2', '');
      this.setAttribute('data-push-active', 'global');

      document.addEventListener('click', this.onExternalToggleClick);
      this.addEventListener('click', this.onInnerClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onExternalToggleClick);
      this.removeEventListener('click', this.onInnerClick);
      document.removeEventListener('keydown', this.onKeydown);
      if (this.isOpen()) {
        this.releaseScrollLock();
        this.releaseFocusTrap();
      }
    }

    isOpen() {
      return this.getAttribute('data-open') === 'true';
    }

    open() {
      if (this.isOpen()) return;

      this.lastFocused = document.activeElement;
      this.setAttribute('data-open', 'true');
      this.syncExternalTriggers(true);
      document.addEventListener('keydown', this.onKeydown);

      // Pass the actually-scrolling element (`.sun-menu-overlay__body`) as the
      // scroll-lock target. The vendor `scroll-lock` lib (themeVendor.ScrollLock
      // in assets/theme.dev.js L774) uses that element to whitelist touch-scroll
      // on iOS — passing the overlay root (which has `overflow: hidden`) left
      // the body un-scrollable in production on mobile.
      const scrollable = this.querySelector('.sun-menu-overlay__body') || this;
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: scrollable}));

      if (window.theme && window.theme.a11y && typeof window.theme.a11y.trapFocus === 'function') {
        const firstFocusable = this.querySelector('[data-sun-menu-close]') || this;
        window.theme.a11y.trapFocus(this, {elementToFocus: firstFocusable});
      }

      this.dispatchEvent(new CustomEvent('sun-menu:open', {bubbles: true}));
    }

    close() {
      if (!this.isOpen()) return;

      this.setAttribute('data-open', 'false');
      this.setActiveN2('');
      this.syncExternalTriggers(false);
      document.removeEventListener('keydown', this.onKeydown);

      this.releaseScrollLock();
      this.releaseFocusTrap();

      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }

      this.dispatchEvent(new CustomEvent('sun-menu:close', {bubbles: true}));
    }

    syncExternalTriggers(isOpen) {
      const triggers = document.querySelectorAll('[data-sun-menu-toggle][aria-controls="' + this.id + '"]');
      triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    }

    releaseScrollLock() {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
    }

    releaseFocusTrap() {
      if (window.theme && window.theme.a11y && typeof window.theme.a11y.removeTrapFocus === 'function') {
        window.theme.a11y.removeTrapFocus();
      }
    }

    onExternalToggleClick(event) {
      const toggle = event.target.closest('[data-sun-menu-toggle]');
      if (!toggle) return;

      const controls = toggle.getAttribute('aria-controls');
      if (controls && controls !== this.id) return;

      event.preventDefault();
      if (this.isOpen()) {
        this.close();
      } else {
        this.open();
      }
    }

    onInnerClick(event) {
      const cartDrawerLink = event.target.closest('a[data-cart-toggle]');
      if (cartDrawerLink && this.contains(cartDrawerLink)) {
        if (typeof window.theme !== 'undefined' && window.theme.settings?.cartType === 'drawer') {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            event.preventDefault();
            event.stopPropagation();
            this.close();
            cartDrawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));
            if (window.a11y) window.a11y.lastElement = cartDrawerLink;
            return;
          }
        }
      }

      const closer = event.target.closest('[data-sun-menu-close]');
      if (closer) {
        event.preventDefault();
        this.close();
        return;
      }

      const back = event.target.closest('[data-sun-menu-back]');
      if (back) {
        event.preventDefault();
        this.setActiveN2('');
        return;
      }

      const n2 = event.target.closest('[data-sun-menu-n2-toggle]');
      if (n2) {
        event.preventDefault();
        const nextKey = n2.getAttribute('data-sun-menu-n2-toggle') || '';
        const currentKey = this.getAttribute('data-active-n2') || '';
        this.setActiveN2(nextKey === currentKey ? '' : nextKey);
      }
    }

    setActiveN2(key) {
      const previousKey = this.getAttribute('data-active-n2') || '';
      this.setAttribute('data-active-n2', key);
      this.setAttribute('data-push-active', key === '' ? 'global' : key);
      this.syncN2Triggers(key);
      this.syncSubPanels(key);
      this.applyPushFallback(key);
      this.manageDrillDownFocus(previousKey, key);
      this.resetBodyScroll(previousKey, key);
    }

    resetBodyScroll(previousKey, nextKey) {
      if (!this.isMobileViewport()) return;
      if (previousKey === nextKey) return;

      const body = this.querySelector('.sun-menu-overlay__body');
      if (body) body.scrollTop = 0;
    }

    manageDrillDownFocus(previousKey, nextKey) {
      if (!this.isMobileViewport()) return;
      if (previousKey === nextKey) return;

      // `{preventScroll: true}` est critique : au moment du focus, la col--2
      // mobile est encore positionnée `left: 100%` (hors-viewport). Sans ce
      // flag, le navigateur déclenche un scroll-into-view de l'élément focusé
      // — ce qui tente de scroller horizontalement le conteneur pour révéler
      // l'élément off-screen et crée un jank perceptible juste avant/pendant
      // l'animation de slide (Claire, "temps de latence dans l'affichage").
      if (nextKey !== '') {
        const panel = this.querySelector('[data-sub-n2="' + CSS.escape(nextKey) + '"]');
        const back = panel && panel.querySelector('[data-sun-menu-back]');
        if (back && typeof back.focus === 'function') back.focus({preventScroll: true});
        return;
      }

      if (previousKey !== '') {
        const trigger = this.querySelector('[data-sun-menu-n2-toggle="' + CSS.escape(previousKey) + '"]');
        if (trigger && typeof trigger.focus === 'function') trigger.focus({preventScroll: true});
      }
    }

    isMobileViewport() {
      return typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 989px)').matches;
    }

    syncN2Triggers(activeKey) {
      const triggers = this.querySelectorAll('[data-sun-menu-n2-toggle]');
      triggers.forEach((trigger) => {
        const key = trigger.getAttribute('data-sun-menu-n2-toggle') || '';
        trigger.setAttribute('aria-expanded', key === activeKey && activeKey !== '' ? 'true' : 'false');
      });
    }

    syncSubPanels(activeKey) {
      const panels = this.querySelectorAll('[data-sub-n2]');
      panels.forEach((panel) => {
        const key = panel.getAttribute('data-sub-n2') || '';
        const isActive = key === activeKey && activeKey !== '';
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
    }

    applyPushFallback(activeKey) {
      if (activeKey === '') return;
      const hasContextual = this.querySelector('[data-push-menu="' + CSS.escape(activeKey) + '"]');
      if (!hasContextual) {
        this.setAttribute('data-push-active', 'global');
      }
    }

    onKeydown(event) {
      if (event.key === ESC_KEY) {
        event.preventDefault();
        this.close();
      }
    }
  }

  customElements.define('sun-menu-overlay', SunMenuOverlay);
})();
